import React, { useState } from "react";
import { X, CreditCard, Shield, Check } from "lucide-react";
import { initiatePayment } from '../lib/stripe';
import { updateUserSubscription } from '../lib/auth';
import { UserRole } from '../lib/types';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planPrice?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ 
  isOpen, 
  onClose, 
  planId,
  planPrice = ""
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const getPlanName = () => {
    switch (planId) {
      case "trial":
        return "Essai Gratuit";
      case "starter":
        return "Starter";
      case "pro":
        return "Pro";
      case "enterprise":
        return "Entreprise";
      default:
        return planId;
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
        throw new Error('La clé publique Stripe n\'est pas configurée');
      }

      await initiatePayment(planId as UserRole);
      // The page will be redirected to Stripe Checkout
      // The success/cancel URLs will handle the rest of the process
    } catch (error: any) {
      console.error('Payment error:', error);
      setError(error.message || 'Une erreur est survenue lors du paiement');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        {isSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Paiement réussi !</h2>
            <p className="text-gray-600">
              Votre abonnement au plan {getPlanName()} a été activé avec succès.
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Finaliser votre abonnement</h2>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Forfait sélectionné</p>
                  <p className="font-semibold text-indigo-800">{getPlanName()}</p>
                </div>
                {planPrice && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Prix mensuel</p>
                    <p className="font-semibold text-indigo-800">{planPrice}€</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">
                {error}
              </div>
            )}
            
            <form onSubmit={handlePayment}>
              <div className="flex items-center mb-6 bg-gray-50 p-3 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm text-gray-600">Paiement sécurisé par Stripe</span>
              </div>
              
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                {isProcessing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    <span>Procéder au paiement</span>
                  </>
                )}
              </button>
            </form>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              En procédant au paiement, vous acceptez nos conditions générales d'utilisation et notre politique de confidentialité.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;